<script lang="ts">
	import { attrs } from '#library/attrs.ts'
	import type { Blok, Timetable } from '#library/components.schema.build.ts'
	import { format_date } from 'kitto'
	import { slide } from 'svelte/transition'

	interface Props {
		blok: Blok<Timetable>
	}

	let { blok }: Props = $props()
	let toggles = $state({ morning: false, afternoon: false })
</script>

{#snippet table(title, times)}
	{#snippet items(items)}
		{#each items as item, i (i)}
			<div class="item">
				<div class="time">
					{format_date('{h}:{mm}{a}', '1970-01-01 ' + item.time)}
				</div>
				<div class="subtitle">
					{item.title}
				</div>
			</div>
		{/each}
	{/snippet}

	<div class="table">
		<span class="title">{title}</span>
		{@render items(times.splice(0, 6))}
		{#if times.length}
			{@const active = toggles[title as keyof typeof toggles]}
			<div class="more">
				<button class="toggle" onclick={() => (toggles[title as keyof typeof toggles] = !active)}>
					Show {active ? 'less' : 'more'}
					<svg class="svg" class:active><use xlink:href="/images/vector.svg#chevron-up"></use></svg>
				</button>
				{#if active}
					<div class="items" transition:slide>
						{@render items(times)}
					</div>
				{/if}
			</div>
		{/if}
	</div>
{/snippet}

<section class="timetable {blok.theme}" {...attrs(blok)}>
	<div class="contain">
		{#if blok.times?.length}
			{@const { morning, afternoon } = blok.times.reduce(
				(acc, item) => (acc[item.time < '12:00' ? 'morning' : 'afternoon'].push(item), acc),
				{ morning: [], afternoon: [] }
			)}
			{#if morning.length}
				{@render table('Morning', morning)}
			{/if}
			{#if afternoon.length}
				{@render table('Afternoon', afternoon)}
			{/if}
		{/if}
	</div>
</section>

<style lang="css">
	.timetable {
		background-color: var(--c-background);
		color: var(--c-foreground);
		padding-block: var(--padding-top, var(--padding-default)) var(--padding-bottom, var(--padding-default));
	}

	.contain {
		display: flex;
		flex-direction: column;
		gap: 2.5rem;
	}

	.table {
		background-color: var(--c-white);
		display: grid;
		gap: 2rem 2.5rem;
		padding: 1.5rem;

		@media (--from-mobile) {
			grid-template-columns: repeat(3, minmax(0, 1fr));
			padding: 2rem;
		}
	}

	.item {
		display: flex;
		flex-direction: column;
		gap: 0.5rem;

		@media (--until-mobile) {
			&:not(:last-of-type)::after {
				background-color: var(--c-foreground);
				content: '';
				display: block;
				opacity: 0.3;
				size: 1px 100%;
				position: absolute;
				inset: auto auto -1rem 0;
			}
		}

		@media (--from-mobile) {
			&:not(:nth-of-type(3n), :last-of-type)::after {
				background-color: var(--c-foreground);
				content: '';
				display: block;
				opacity: 0.3;
				size: 100% 1px;
				position: absolute;
				inset: 0 0 auto auto;
			}
		}
	}

	.title {
		color: var(--c-midground);
		font: 600 var(--f-h4);
		grid-column: 1 / -1;
		letter-spacing: var(--ls-h4);
	}

	.time {
		font: 400 var(--f-h3);
		letter-spacing: var(--ls-h3);
	}

	.subtitle {
		color: var(--c-midground);
		font: 400 var(--f-body1);
		letter-spacing: var(--ls-body1);
		white-space: pre-wrap;
	}

	.toggle {
		align-items: center;
		display: flex;
		gap: 0.5rem;
		font: 400 var(--f-body1);
		grid-column: 1 / -1;
		letter-spacing: var(--ls-body1);
		text-align: start;
	}

	.svg {
		rotate: 180deg;
		size: 1.25rem;
		transition: rotate 0.2s ease;

		&.active {
			rotate: 0deg;
		}
	}

	.more {
		grid-column: 1 / -1;
	}

	.items {
		display: grid;
		gap: 2rem 2.5rem;

		@media (--from-mobile) {
			grid-template-columns: repeat(3, minmax(0, 1fr));
		}
	}
</style>
